import { api } from '../api';
import {
  USER_HISTORY_ACTIVITY,
  USER_ACTIVE_DEVICE,
  USER_REVOKE_DEVICE,
  RESET_PASSWORD,
  ACTIVE_GA,
  ACTIVE_GA_VERIFY,
  ACTIVE_MFA_RESEND,
  ACTIVE_EMAIL,
  ACTIVE_EMAIL_VERIFY,
  ACTIVE_SMS,
  ACTIVE_SMS_VERIFY
} from '../../constants';
import {
  ActiveEmailVerifyRequest,
  ActiveEmailVerifyResponse,
  ActiveGaResponse,
  ActiveGaVerifyRequest,
  ActiveGaVerifyResponse,
  ActiveSmsVerifyRequest,
  ActiveSmsVerifyResponse,
  GetUserActiveDeviceResponse,
  GetUserHistoryActivitiesResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UserRevokeDeviceRequest,
  UserRevokeDeviceResponse
} from './types';

export const securitySlice = api.injectEndpoints({
  endpoints: (build) => ({
    userHistoryActivity: build.query<
      GetUserHistoryActivitiesResponse,
      { page: number; limit: number; infinit: boolean }
    >({
      query: ({ page, limit }) => `${USER_HISTORY_ACTIVITY}/${limit}/${page}`,
      transformResponse: (
        response: GetUserHistoryActivitiesResponse,
        meta,
        arg
      ) => {
        Object.assign(response.data, {
          ...response.data,
          infinit: arg.infinit
        });
        return response;
      },
      merge: (currentCache, newItems) => {
        const isInfinit = newItems.data.infinit;
        if (isInfinit) {
          currentCache?.data?.info?.push(...newItems.data.info);
        } else {
          currentCache.data = newItems.data;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      }
    }),
    userActiveDevices: build.query<GetUserActiveDeviceResponse, void>({
      query: () => USER_ACTIVE_DEVICE
    }),
    revokeDevice: build.mutation<
      UserRevokeDeviceResponse,
      UserRevokeDeviceRequest
    >({
      query: (data) => {
        return {
          url: USER_REVOKE_DEVICE,
          method: 'POST',
          body: data
        };
      }
      // providesTags: ['userActiveDevices']
    }),
    resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data) => {
        return {
          url: RESET_PASSWORD,
          method: 'POST',
          body: data
        };
      }
    }),
    activeGa: build.mutation<ActiveGaResponse, void>({
      query: () => {
        return {
          url: ACTIVE_GA,
          method: 'POST',
          body: {}
        };
      }
    }),
    activeGaVerify: build.mutation<
      ActiveGaVerifyResponse,
      ActiveGaVerifyRequest
    >({
      query: (data) => {
        return {
          url: ACTIVE_GA_VERIFY,
          method: 'POST',
          body: data
        };
      }
    }),
    activeEmail: build.mutation<void, void>({
      query: () => {
        return {
          url: ACTIVE_EMAIL,
          method: 'POST',
          body: {}
        };
      }
    }),
    activeEmailVerify: build.mutation<
      ActiveEmailVerifyResponse,
      ActiveEmailVerifyRequest
    >({
      query: (data) => {
        return {
          url: ACTIVE_EMAIL_VERIFY,
          method: 'POST',
          body: data
        };
      }
    }),
    activeSms: build.mutation<void, void>({
      query: () => {
        return {
          url: ACTIVE_SMS,
          method: 'POST',
          body: {}
        };
      }
    }),
    activeSmsVerify: build.mutation<
      ActiveSmsVerifyResponse,
      ActiveSmsVerifyRequest
    >({
      query: (data) => {
        return {
          url: ACTIVE_SMS_VERIFY,
          method: 'POST',
          body: data
        };
      }
    }),
    activeMfaResendCode: build.query<void, void>({
      query: () => ACTIVE_MFA_RESEND
    })
  }),
  overrideExisting: false
});

export const {
  useUserHistoryActivityQuery,
  useLazyUserHistoryActivityQuery,
  useUserActiveDevicesQuery,
  useRevokeDeviceMutation,
  useResetPasswordMutation,
  useActiveGaMutation,
  useActiveGaVerifyMutation,
  useActiveEmailMutation,
  useActiveEmailVerifyMutation,
  useActiveSmsMutation,
  useActiveSmsVerifyMutation,
  useLazyActiveMfaResendCodeQuery
} = securitySlice;
